/**
 * Helpers for re-emitting the legacy response shapes the existing components
 * still parse.
 *
 * The bridge is deliberately bug-compatible on the wire it produces while being
 * correct underneath: a component that checks
 * `String(res.data.status).toLowerCase().includes('success')` keeps working,
 * even though the call it made was a real REST request with bearer auth, real
 * status codes, and no identity in the body.
 *
 * Every function here is debt with a known owner. As a component migrates to a
 * native `api2/*` client, its shapes stop being referenced; when a helper has
 * no callers left, delete it.
 */

import { REFUSAL_CODES } from '@/api/refusals';

// --- Legacy success envelopes ---------------------------------------------
// keyon used at least seven of these, chosen per-endpoint with no rule.

export const okSuccessful = (extra = {}) => ({ status: 'Successful', ...extra });
export const okStatus = (text, extra = {}) => ({ status: text, ...extra });
export const okMessage = (text, extra = {}) => ({ message: text, ...extra });
export const okMsg = (text, extra = {}) => ({ msg: text, ...extra });
export const okCode1 = (id = null, extra = {}) => ({ code: 1, id, ...extra });
export const records = (rows, extra = {}) => ({ records: rows || [], ...extra });
export const dataEnvelope = (payload, extra = {}) => ({
  status: 'Successful',
  data: payload,
  ...extra,
});

/**
 * The codes main's upload, delete and file refusals carry
 * (keyon/lib/UploadRefusal.php, keyon/lib/PostSizeGuard.php, keyon/file.php),
 * which the API sends with the same spelling and the same sentences
 * (pacmny-api App\Domain\Documents\UploadRefusal).
 */
export const MAIN_REFUSAL_CODES = new Set([
  'missing_file', 'upload_error', 'unsupported_type', 'invalid_attachment', 'too_many_attachments',
  'invalid_category', 'file_and_uid', 'invalid_file_uid', 'mfa_required', 'already_deleted',
  'already_claimed', 'reason_required', 'directive_finalized', 'document_removed', 'endpoint_retired',
  'too_large', 'rate_limited', 'uploads_unavailable', 'bundle_too_large', 'payload_too_large',
  'scan_pending', 'cross_site', 'quarantined', 'missing', 'unavailable',
]);

/**
 * The API's own refusal codes, in main's vocabulary (src/api/refusals.js).
 *
 * Where the port names a refusal main already had a word for, the bridge
 * speaks main's word, so a component written against main's refusalMessage()
 * recognises it: a missing permission and a token that was never granted the
 * scope are both main's `forbidden`. Anything already in main's list passes
 * through under its own name.
 */
export const PORT_REFUSAL_SYNONYMS = Object.freeze({
  insufficient_permission: 'forbidden',
  insufficient_scope: 'forbidden',
  throttled: 'too_many_requests',
  // The signing refusals (OrderSigning, OrderAuthorship): an account type that does not sign, a signer without the
  // licence or NPI the half asserts, one without the signing permission, and an account with no name to sign a release
  // or a change order with. Main shows each in the server's words as a refusal of its own, which a person who cannot
  // sign needs to read; without a main word a 403 became "You do not have permission to view this."
  signer_type_not_allowed: 'forbidden',
  credential_required: 'forbidden',
  permission_required: 'forbidden',
  unsigned: 'forbidden',
});

/** Main's code for this error, when it is a refusal main shows in the server's words; else null. */
export function mainRefusalCode(err) {
  if (!err || typeof err.code !== 'string') return null;
  const code = PORT_REFUSAL_SYNONYMS[err.code] || err.code;
  return REFUSAL_CODES.includes(code) ? code : null;
}

/**
 * Re-express an ApiError in whichever failure shape the call site expects.
 *
 * Note what is NOT happening: the bridge does not swallow the error. It throws
 * for transport-level failures and only shapes *domain* failures the component
 * is equipped to read. Legacy returned HTTP 200 for everything, so ~73 call
 * sites treat any response as success — those components are exactly the ones
 * §7 of the migration plan schedules for rework.
 */
export function legacyError(err, style = 'status') {
  // A text or call (Twilio.SendText / SendPhoneCall / SendConferenceText):
  // keyon's failure body, {status:'Error', code, message, TwilioStatus:'failed'}
  // (keyon/lib/TwilioAccess.php refusalBody/failureBody), with the API's own
  // code and sentence. src/api/smsApi.js shows "sent" only on a SID with
  // TwilioStatus 'successful', and otherwise refusalMessage() — which needs the
  // code (recipient_not_on_file, forbidden, caller_not_on_file, sms_failed...)
  // and the sentence. The generic arm below would rewrite every 403 to "You do
  // not have permission to view this.", which is not what a person told "You
  // do not have permission to text or call patients." needs to read.
  if (style === 'twilio') {
    const sentence = (err && err.message) || 'The request could not be completed.';

    return {
      status: 'Error',
      code: (err && err.code) || 'error',
      message: sentence,
      error: sentence,
      TwilioStatus: 'failed',
      data: null,
      results: [],
      denied: Boolean(err && err.status === 403),
    };
  }

  // Main's refusals arrive in main's one body, whatever this call site's
  // legacy style: keyon answers every upload, delete and file refusal as
  // {status:'Error', code, message, error} with a real HTTP status
  // (keyon/lib/UploadRefusal.php), and src/api/refusals.js reads the code and
  // the sentence from exactly those keys. The API sends the same codes and
  // sentences in its own envelope, so re-emitting main's body here is what
  // lets "That upload is too large..." reach the person instead of a generic
  // failure.
  if (err && MAIN_REFUSAL_CODES.has(err.code)) {
    const sentence = err.message || 'This request could not be completed.';
    // Main's body carries a refused attachment's position and why
    // (UploadRefusal::body extra keys `index`, `reason`); the API sends them
    // as the error's details.
    const details = err.details && typeof err.details === 'object' ? err.details : {};
    const extra = {};

    if (details.index !== undefined) extra.index = details.index;
    if (details.reason !== undefined) extra.reason = details.reason;

    return {
      ...extra,
      status: 'Error',
      code: err.code,
      message: sentence,
      error: sentence,
      data: null,
      results: [],
      denied: err.status === 403,
    };
  }

  // Every other refusal main shows in the server's own words (forbidden,
  // unauthenticated, too_many_requests, locked, impersonation_forbidden...):
  // the call site's shape, plus the code and the sentence. Dropping the code
  // here is what left refusalMessage() matching nothing that came through the
  // bridge, so every 403 read "You do not have permission to view this."
  // whatever the API had actually said.
  //
  // The 'code' shape keeps its number: its callers test `code` for 1 or 0, so
  // a word there would read as success. The word goes in `refusal_code`, which
  // refusalMessage() also reads.
  const refusal = mainRefusalCode(err);

  if (refusal !== null) {
    const sentence = err.message || 'This request could not be completed.';
    const known = { error: sentence, data: null, results: [], denied: err.status === 403 };

    switch (style) {
      case 'code':    return { code: 0, refusal_code: refusal, ...known };
      case 'msg':     return { msg: sentence, status: 'Error', code: refusal, ...known };
      case 'message': return { message: sentence, status: 'Error', code: refusal, ...known };
      case 'status':
      default:        return { status: 'Error', message: sentence, code: refusal, ...known };
    }
  }

  const denied = err && err.status === 403;
  const message = denied
    ? 'You do not have permission to view this.'
    : (err && err.message ? err.message : 'Request failed');

  // `data` and `results` are on every shape deliberately.
  //
  // Legacy always answered HTTP 200, so call sites read the outcome out of the
  // body — and roughly 73 of them go straight for `res.data.data.status` or
  // `.results.length` without checking. Omitting the key turned a clean 403
  // into "Cannot read properties of undefined (reading 'status')", which the
  // component then alerted at the user: the authorization layer worked and the
  // UI reported it as a crash. Present-and-empty lets those call sites reach
  // their own "nothing here" branch.
  const empty = { data: null, results: [], denied };

  switch (style) {
    case 'code':    return { code: 0, error: message, ...empty };
    case 'msg':     return { msg: message, status: 'Error', ...empty };
    case 'message': return { message, status: 'Error', ...empty };
    case 'status':
    default:        return { status: 'Error', message, ...empty };
  }
}

// --- Scalar coercions ------------------------------------------------------
// Legacy stored booleans as varchar and shipped them a dozen different ways.

const TRUTHY = new Set([1, '1', 'Y', 'y', 'true', 'TRUE', 'True', true, 'yes', 'Yes']);

export const bool = (v) => TRUTHY.has(v);
export const intBool = (v) => (v ? 1 : 0);
export const strBool = (v) => (v ? '1' : '0');

export const id = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
export const idStr = (v) => (v === null || v === undefined ? null : String(v));

/**
 * A value that may be used as a route-model-binding segment, or `null`.
 *
 * `id()` is `Number()`, and `Number('N42')` is `NaN` — which stringifies into a
 * path as the literal `/orders/NaN/signatures`. That is not an unresolved
 * segment as far as `legacyBridge` is concerned, so it used to be *sent*, and
 * the API answered 404 with no clue why. Anything that is not a positive
 * integer is "no record addressed" and comes back as `null`, which the bridge's
 * unresolved-segment guard does recognise.
 *
 * Gen-2 order numbers are `'N' + orderId` (design 03 §8.2), so they are exactly
 * the values that produce `NaN` here.
 */
export function routeId(v) {
  const n = id(v);

  return n === null || !Number.isInteger(n) || n <= 0 ? null : n;
}

/**
 * The order id a per-order route binds, out of a legacy payload.
 *
 * Every keyon per-order operation names its target `ordernumber`, and the
 * legacy `orders` table was keyed on that string. The port keys orders on a
 * global auto-increment `id` and allocates `order_number` **per organization**
 * (`App\Domain\Orders\OrderNumbers`), so the two coincide only in a fresh
 * single-tenant database. Feeding a display number into `{order}` therefore
 * resolves a *different* order of the same tenant — a signature or a
 * discontinuation on the wrong document — as soon as a second organization
 * exists.
 *
 * The bridge's own row shapers (`orderRows`, `medicationRows`) put the API's
 * `id` into every "which order" spelling for exactly this reason: what the
 * components round-trip back to us is the routing key, and the human-facing
 * number travels separately as `order_display_number`. `orderid` / `order_id`
 * are read first because a few call sites pass the row's id column straight
 * through (OrdersPage.vue:1165).
 */
export function orderRef(payload) {
  const p = payload || {};

  return routeId(p.orderid)
    ?? routeId(p.order_id)
    ?? routeId(p.ordernumber)
    ?? routeId(p.ordnum);
}

/**
 * A comma list, an array, or a single scalar, as an array of numeric ids.
 *
 * The legacy wire carried recipient lists three ways depending on the widget
 * that built them — `'12,13'` from the message widgets, `['12','13']` from the
 * note picker, a bare `'12'` from the one-to-one composer — and the new
 * endpoints validate `array` with `integer` members, so a comma string fails
 * the rule rather than being split.
 */
export function idList(value) {
  const raw = Array.isArray(value) ? value : String(value ?? '').split(',');

  return raw
    .map((entry) => Number(String(entry).trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * The `File` objects out of whatever the call site appended under one key.
 *
 * `formdata.append('files', input.files[0])` with no file selected appends the
 * *string* `"undefined"`, which the API's `files.*` => `file` rule rejects with
 * a message about a field the user never filled in. A `FileList` and a plain
 * array both flatten, so a picker that grows to multi-select needs no change
 * here.
 */
export function fileList(value) {
  if (value === null || value === undefined || value === '') return [];

  const many = Array.isArray(value) || (typeof FileList !== 'undefined' && value instanceof FileList);
  const raw = many ? Array.from(value) : [value];

  return raw.filter((entry) => typeof File !== 'undefined' && entry instanceof File);
}

/**
 * A JSON-encoded child collection, as an array.
 *
 * `InsertNewOrder` posts its medication, diagnosis and procedure lines as
 * `JSON.stringify(...)` strings, which keyon decoded server-side
 * (tswebhook.php:8631). `StoreOrderRequest::normaliseRows` returns `[]` for
 * anything that is not an array and the `sometimes|array` rules then pass, so an
 * unparsed string became an order with no lines and a success message. Parsing
 * here is what the legacy did one hop later.
 */
export function jsonRows(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || value.trim() === '') return [];

  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // Unreachable for anything `JSON.stringify` produced, which is every real
    // caller. Passed through rather than turned into `[]` so this can never
    // become a second way to lose the lines silently.
    return value;
  }
}

// --- Medication schedules --------------------------------------------------

/**
 * Dose slots, as the `'HH:MM'` strings every schedule endpoint validates.
 *
 * The legacy wire format for `slotedtimes` is `[{time, status, dosage}]` and the
 * legacy handlers read `$stime->time` (tswebhook.php:1423). The port validates
 * `times.*` as a bare time string — `ScheduleTemplate::timeRule()` — so the
 * object array 422s on every administration-schedule write. Status and dosage
 * are not lost information: the port derives a new slot's status itself and
 * takes the dose from the medication.
 *
 * Also accepts the `'08:00, 20:00'` string a couple of call sites send as
 * `scheduletimes`/`administrationTimes`.
 */
export function scheduleTimes(value) {
  if (value === null || value === undefined || value === '') return undefined;

  const raw = (Array.isArray(value) ? value : String(value).split(','))
    .filter((entry) => entry !== null && entry !== undefined && entry !== '');
  const seen = new Set();

  raw.forEach((entry) => {
    const time = entry && typeof entry === 'object' ? entry.time : entry;
    const text = String(time ?? '').trim();
    // `9:05` and `08:00:00` both reach here; the rule is a strict `HH:MM`.
    const match = /^(\d{1,2}):(\d{2})/.exec(text);

    if (!match) return;

    const hours = Number(match[1]);

    if (hours > 23) return;

    seen.add(`${String(hours).padStart(2, '0')}:${match[2]}`);
  });

  // An empty list is a real instruction on `PUT .../schedule` — "remove every
  // pending slot" — so it must only ever come from an empty *input*. Slots that
  // were sent but did not parse fall back to omitting the field, which the
  // `required` rule refuses: a 422 the nurse can see beats silently wiping a
  // medication's administration times.
  if (raw.length > 0 && seen.size === 0) return undefined;

  return [...seen];
}

/**
 * The `times` a hold applies to, from the legacy hold *scope*.
 *
 * `holdType` is `'all'` or `'specific'` and says how much of the day the hold
 * covers — it is not the port's `type`, which says hold-versus-discontinue. The
 * port spells "every slot" as an absent `times`, so `'all'` must send nothing
 * rather than an empty array (which would mean "no slots at all").
 */
export function holdTimes(scope, times) {
  return String(scope || '').toLowerCase() === 'specific' ? scheduleTimes(times) : undefined;
}

// --- The MAR grid ----------------------------------------------------------

/**
 * `medication_administration_times.status` against the literals the MAR grid
 * renders.
 *
 * The legacy carried state and reason in one compound literal — `'hold-medtime'`,
 * `'discontinue-archive'` — and `DoseStatusTag`'s own docblock records that
 * split. `held` + tag `hold` IS the legacy `'hold-medtime'`, so that is what the
 * grid is given: MedAdmin locks a cell on `'hold'`, `'hold-medtime'` and
 * `'discontinue-medtime'` (MedAdmin.vue:3769, :4115) and a bare `'discontinue'`
 * is NOT in the scheduled path's lock list, so a discontinued dose spelt that
 * way would render as an editable, signable cell.
 *
 * `missed` has no legacy literal — a dose nobody gave simply stayed blank — so
 * it travels under its own name rather than being folded into `refused`, which
 * would assert a refusal that never happened. It renders unlocked and unstyled,
 * which is what the legacy grid did with the same fact.
 */
const MAR_CELL_STATUSES = {
  pending: 'pending',
  taken: 'taken',
  refused: 'refused',
  held: 'hold-medtime',
  discontinued: 'discontinue-medtime',
};

const marCellStatus = (v) => MAR_CELL_STATUSES[String(v ?? '').toLowerCase()] ?? v ?? '';

/** One MAR cell, in the `medlogtimes` entry shape MedAdmin builds its grid from. */
function marSlot(cell) {
  return {
    // PRN doses have no scheduled slot, only the clock time they were given at
    // (`scheduled_slot_key` is NULL for them by design), and the grid keys every
    // entry on `time`.
    time: cell.scheduled_time || cell.taken_at_time || '',
    takentime: cell.taken_at_time || null,
    status: marCellStatus(cell.status),
    // Read by `getBgColor` per time slot (MedAdmin.vue:1192), which is why the
    // tag travels per cell rather than per medication.
    tagstatus: cell.status_tag || '',
    reason: cell.reason ?? null,
    dosage: cell.dose_amount ?? null,
    // The grid filters `medlogtimes` on this before rendering a day's column
    // (MedAdmin.vue:3725), so a cell without it would be shown under every date.
    administerdate: cell.administered_on,
    holdstartdate: cell.hold_start_on ?? null,
    holdenddate: cell.hold_end_on ?? null,
    marid: cell.id,
  };
}

/**
 * Medication rows with their MAR cells folded back in, per day and per slot.
 *
 * The legacy read returned both facts in one payload:
 * `findpatientactiveMedOrdersByStrtEndDate` (SqlClass.php:5428-5512) aggregated
 * `medicationlog` into a `medlogtimes` JSON array per administration date and a
 * flat `time_status_pairs` list per medication. The port split the read in two
 * on purpose — the old one ran hold and discontinue *maintenance* as a side
 * effect, so opening a chart's medication tab mutated it — so the two halves are
 * `GET /patients/{p}/medications` and `GET /patients/{p}/medication-administrations`
 * and this is where they are put back together.
 *
 * Without it the grid receives bare medication rows: no slots, no taken times,
 * no hold windows, and every cell renders as an unfilled scheduled dose over a
 * day that may already have been signed.
 *
 * `archived` cells are dropped. They are the superseded history of a slot that
 * was re-recorded, and the grid has no concept of two entries for one slot — it
 * matches the first medlog entry per scheduled time (MedAdmin.vue:3745) and
 * would render whichever happened to sort first.
 *
 * **The hold window comes from the organization's today only.** A row carries main's
 * `medholdstartdate` / `medholdenddate` (the days its held cells were held
 * under, comma-joined, '' when nothing is held) only when `ofToday` says the
 * cells are the organization's today and no other day: the MAR read made with no
 * window, which the API answers for the organization's calendar day. Main's
 * findpatientactiveMedOrders builds them from the cells of today alone
 * (`administerdate = :tddt`) and its date-range query builds none. A range holds
 * the cells of days gone by, and a cell stays held after its hold is released
 * ("yesterday's held cells are left alone: they record that the dose was not
 * given", MedicationHoldService::clearCurrent). A window taken from them showed a
 * released hold on a line that is active again, and the next dose edit sent it
 * back as a new hold starting in the past, which the API refuses
 * (hold_starts_in_past) along with the whole edit (P-NURSE-H12). The held cells
 * of today are the holds in force: a released hold's are ordinary again, and the
 * window of one still in force is the one the API takes as no new hold.
 */
export function withMarSlots(rows, cells, { ofToday = false } = {}) {
  const byMedication = new Map();
  // The hold windows the held cells of today carry, per medication (main's medholdstartdate / medholdenddate).
  const heldWindows = new Map();

  (Array.isArray(cells) ? cells : []).forEach((cell) => {
    const medication = Number(cell && cell.order_medication_id);
    const day = cell && cell.administered_on;

    if (!Number.isFinite(medication) || !day || cell.status === 'archived') return;

    const days = byMedication.get(medication) || new Map();

    days.set(day, [...(days.get(day) || []), marSlot(cell)]);
    byMedication.set(medication, days);

    if (ofToday && cell.status === 'held') {
      const window = heldWindows.get(medication) || { starts: new Set(), ends: new Set() };

      if (cell.hold_start_on) window.starts.add(String(cell.hold_start_on).slice(0, 10));
      if (cell.hold_end_on) window.ends.add(String(cell.hold_end_on).slice(0, 10));
      heldWindows.set(medication, window);
    }
  });

  return (Array.isArray(rows) ? rows : []).map((row) => {
    const days = byMedication.get(Number(row.id));

    if (!days) return row;

    const administerdate = [...days.entries()]
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([day, slots]) => ({
        administerdate: day,
        medlogtimes: [...slots].sort((a, b) => String(a.time).localeCompare(String(b.time))),
      }));
    const window = heldWindows.get(Number(row.id));

    return {
      ...row,
      // Main's GROUP_CONCAT over the hold dates of today's slots, distinct and oldest first, or '' when nothing today
      // is held - and always '' for a read of a range (see above). The edit form shows its hold label and its Med Hold
      // Start / End inputs only when the start is not empty (AddMedicationForm ifMedholdtime), and heldSlotLabel reads
      // the span from these; the port's rows carried neither, so the label and the inputs never appeared (NUR339-16).
      medholdstartdate: window ? [...window.starts].sort().join(',') : '',
      medholdenddate: window ? [...window.ends].sort().join(',') : '',
      administerdate,
      // The same cells again, flat. The grid reads this one for PRN medications,
      // where there is no schedule to hang a day's column off and each entry
      // carries its own date (MedAdmin.vue:3587).
      time_status_pairs: administerdate.flatMap((day) => day.medlogtimes),
      // Main's `tagstatus`, the GROUP_CONCAT of the cells' tags in the window:
      // the pre-pour sheet reads "New" from it (PP-08, derivedStatusOf).
      tagstatus: [...new Set(administerdate.flatMap((day) => day.medlogtimes.map((slot) => slot.tagstatus)).filter(Boolean))].join(','),
    };
  });
}

/**
 * A medication's scheduled dose times, as the `[{time}]` array `yearmedtime` is.
 *
 * `?include=administration_schedule` answers with `ScheduleTemplate::parse`'s
 * `[{time, dose, status}]`, which is already the legacy shape — this only
 * unwraps it, and returns an empty list rather than `undefined` so the grid's
 * `JSON.parse(med.yearmedtime || '[]')` fallback is never reached with a value
 * it cannot parse.
 */
export function scheduleSlots(row) {
  const schedule = row && row.administration_schedule;

  return schedule && Array.isArray(schedule.slots) ? schedule.slots : [];
}

/**
 * A calendar day as "YYYY-MM-DD" in the viewer's **local** zone.
 *
 * `dateOnly` gives the UTC day of an instant, and from 20:00 in New York the
 * UTC day is already tomorrow: a hold starting today was sent as starting
 * tomorrow and its end a day late (main #335, PP-22), and a dose signed after
 * 20:00 was filed under tomorrow. The hold, dose sign-off and order sign-off
 * paths use this instead.
 *
 * A bare "YYYY-MM-DD" is already a calendar day and is returned as it is —
 * `new Date('2026-10-15')` parses as UTC midnight, which is the 14th in every
 * US zone, so parsing it would move the day backwards.
 */
export function toLocalYMD(v) {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return v.trim();
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The hold window a MAR row's edit sends back: `hold_start_date` / `hold_end_date`, from `medholdstartdate` /
 * `medholdenddate`.
 *
 * Those arrive as main's GROUP_CONCAT: the distinct days of the row's held cells, comma-joined (withMarSlots). One
 * hold is one start and at most one end, and that is what the API takes. A row held under several windows names no
 * single one, so it sends none - the API would otherwise read the start of one hold with the end of another as a new
 * hold - and an end without a start is not a window.
 *
 * @returns {{hold_start_date?: string, hold_end_date?: string}}
 */
export function holdWindow(payload) {
  const days = (list) => [...new Set(String(list || '').split(',').map((d) => d.trim()).filter(Boolean))];
  const starts = days(payload && payload.medholdstartdate);
  const ends = days(payload && payload.medholdenddate);

  if (starts.length !== 1 || ends.length > 1) return {};

  return {
    hold_start_date: toLocalYMD(starts[0]) || undefined,
    hold_end_date: ends.length ? toLocalYMD(ends[0]) || undefined : undefined,
  };
}

/**
 * A wall-clock time as "HH:MM" (24-hour), from "21:30", "9:30", "21:30:00"
 * or "9:30 PM"; null when the value is not a time.
 *
 * Sent with the local day, it is what the API composes into an instant in the
 * **organization's** zone (`StorePrnAdministrationRequest`). A naive
 * "YYYY-MM-DDTHH:MM" instant is read as UTC, so a PRN dose charted at 21:30 in
 * New York was filed five hours early — and its day was the UTC one.
 */
export function wallClockTime(v) {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/.exec(String(v ?? '').trim());
  if (!match) return null;
  let hours = Number(match[1]);
  const meridiem = match[3] ? match[3].toLowerCase() : null;
  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;
  if (hours > 23) return null;
  return `${String(hours).padStart(2, '0')}:${match[2]}`;
}

/**
 * The port's medication statuses in the words main's screens compare against
 * (MED-15). The pre-pour sheet's STATUS_MAP and the MAR's status select know
 * main's spellings only, so `active_pending`, `on_hold` and `superseded` read as
 * "Active" there — a held or superseded line shown as pourable and givable.
 * `superseded` is main's `Archived`: a line a change replaced.
 */
export const LEGACY_STATUS_MAP = {
  active: 'Active',
  active_pending: 'Active-Pending',
  pending: 'Pending',
  on_hold: 'Hold',
  discontinued: 'Discontinued',
  completed: 'Completed',
  superseded: 'Archived',
};

/**
 * The port's order statuses in the words main's order grids print (P-ORD2F O5).
 *
 * Main's grids printed the orders table's own columns: `status` as keyon wrote
 * it - the signature ladder's 'Pending', 'Active-Pending' and 'Active' - and
 * `ordertype` as the order form's select stored it. The screens still compare
 * and style by those words (the Orders page's New tab tests `=== 'Pending'`;
 * brand-ds.scss colours a badge by `.status-Active-Pending`), so every order
 * grid takes them from here, through `orderRows` (bridgeMap.js), the one shaper
 * they all go through. A status the port alone has gets the word main's
 * medication rows give it (`superseded` is `Archived`, as in LEGACY_STATUS_MAP)
 * or its plain name (`cancelled`: main's orders table has no such state).
 *
 * These are the words of the ladder and of the statuses beside it. A hold or
 * discontinue order on the ladder is worded by what it is instead
 * ({@link ORDER_CHANGE_STATUS_WORDS}, chosen in `orderStatusWord`).
 */
export const ORDER_STATUS_WORDS = Object.freeze({
  pending: 'Pending',
  active_pending: 'Active-Pending',
  active: 'Active',
  discontinued: 'Discontinued',
  superseded: 'Archived',
  cancelled: 'Cancelled',
});

/**
 * What main's `orders.status` kept for a hold or discontinue order, by the
 * `change_type` the port records it under (P-ORD2F O5).
 *
 * Main's hold and discontinue orders never climbed the signature ladder: they
 * were written with the status 'Hold' / 'discontinue' and kept it through both
 * signatures (SqlClass.php UpdateOrderwithSignature moves only the ladder's
 * words: "a hold or discontinue order keeps its own status"), and the pending
 * lists fetched them by those words (PENDING_ORDER_STATUS_IN). So every grid
 * printed 'Hold' / 'discontinue' for them - the home pending queue, where such
 * an order has no medication line to name it and the status is the one sign of
 * what it is - and the Orders page tests the word
 * (`order.status !== 'discontinue'`). The port's order is on the ladder like
 * any other and says what it is in `change_type`.
 */
export const ORDER_CHANGE_STATUS_WORDS = Object.freeze({
  hold: 'Hold',
  discontinue: 'discontinue',
});

/**
 * The rungs of the signature ladder: the statuses an order has while it is
 * signed and live. Only these give way to a hold or discontinue order's own
 * word; an order that has left the ladder (replaced, cancelled, discontinued)
 * is no longer what it was raised as, and says where it went.
 */
const LADDER_STATUSES = Object.freeze(['pending', 'active_pending', 'active']);

/** What the order form's select stored in main's `orders.ordertype`, which the grids' Order Type column prints. */
export const ORDER_TYPE_WORDS = Object.freeze({
  physician: 'Physicians Order',
  nurse: 'Nurses Order',
  verbal: 'Verbal Order',
});

const hasWord = (words, key) => typeof key === 'string' && Object.prototype.hasOwnProperty.call(words, key);
const wordFor = (words, value) => (hasWord(words, value) ? words[value] : value);

/**
 * An order's status in main's word; a value with no word of its own (main's own, or unknown) is returned as it is.
 *
 * `changeType` is the order's `change_type`: given it, a hold or discontinue order on the ladder says 'Hold' /
 * 'discontinue' (ORDER_CHANGE_STATUS_WORDS) where every other order says the word of its status. Every place that
 * words an order's status goes through here with the order's change type, so the grids and the sign-off popup agree.
 */
export const orderStatusWord = (status, changeType) => (
  LADDER_STATUSES.includes(status) && hasWord(ORDER_CHANGE_STATUS_WORDS, changeType)
    ? ORDER_CHANGE_STATUS_WORDS[changeType]
    : wordFor(ORDER_STATUS_WORDS, status)
);

/** An order's type in main's word; a value with no word of its own (main's own, or unknown) is returned as it is. */
export const orderTypeWord = (type) => wordFor(ORDER_TYPE_WORDS, type);

/**
 * The ICD code out of a diagnosis label (MED-26).
 *
 * AddMedicationForm's DiagnosisCombobox saves the diagnosis as "CODE -
 * description" (main's `fullDiag`), and the API keeps only the code on the line
 * (`diagnosis_code`, at most 32 characters), so a long label was a 422 on every
 * save that picked a diagnosis. A value with no " - " is passed as it is.
 */
export function diagnosisCode(value) {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  if (text === '') return undefined;
  const [code] = text.split(' - ');
  return code.trim();
}

/**
 * The order a medication line waits on while that is not its own order, or `null` (P-ORD2F O2).
 *
 * A released course waits on its release order (NUR339-02), and a line with a hold a clinician has just placed waits
 * on the hold's order: each is half signed while the order the line was prescribed under is signed. The API names them
 * only while they are half signed (`?include=release_order,hold_order`), so a line that waits on neither has none.
 * The release is read first.
 *
 * Two readers must agree on it, which is why it is one function: `medicationRows` greys the row from this order's
 * status and signatures, and the sign-off lookup (api2/orderSignOff.js) opens this order when the row's signature card
 * is clicked. The card is only ever for the order that greys the row.
 */
export function waitingOrderOf(row) {
  return (row && (row.release_order || row.hold_order)) || null;
}

/**
 * A medication row, in the legacy spellings.
 *
 * The medication grids, the MAR and the pre-pour sheet read the legacy column
 * names; `MedicationResource` names every one of them differently. Every list
 * of medications the bridge answers goes through here, so the aliases cannot
 * drift between screens.
 *
 * `order_number` / `ordernumber` are the *order this line belongs to*, and they
 * are the id, not a number: `MedicationResource` emits `order_id` and nothing
 * else, while eleven MAR call sites read `medication.order_number` and post it
 * straight back as the target of a hold, a discontinuation, a schedule rewrite
 * or a signature lookup. Left unmapped they were `undefined`, which MedAdmin
 * reports to the nurse as "Order Number associated with this change does not
 * exist" (MedAdmin.vue:4379) — every hold and discontinue from the MAR refused
 * before it was sent. Nothing else may stand in for it: the order's display
 * number is a per-organization sequence, and routed as an id it addresses a
 * different order (see `orderRows` in bridgeMap.js). A line with no order — an
 * un-ordered or non-prescription medication — carries the empty string, which
 * is what main's screens test for.
 *
 * Superseded lines are kept here. The MAR's date-range read, the order grids
 * and the edit view must still show the version a dose was given under; only
 * the views of what is *current* (`currentCourseRows`) drop them.
 */
export function medicationRows(rows) {
  return (rows || [])
    .filter((row) => row)
    .map((row) => {
      const legacyStatus = LEGACY_STATUS_MAP[row.status] || row.status || '';
      // The order whose signatures the row waits on. A released course waits on its release order (NUR339-02), which
      // is half signed while the order it was prescribed under is signed: main's released row is Pending under the
      // release order, and `ifOrderSigned` reads the status and signatures of THAT to disable the row's controls.
      // A line with a hold a clinician has just placed waits on the hold's order the same way (P-ORD2F O2): main's
      // row is a copy under the hold's own order, Pending for a nurse's hold and Active-Pending for a provider's,
      // so the whole row is disabled until the other half signs. The API names that order only while it is half
      // signed (`?include=hold_order`), so a line the hold has been signed off for reads its own order again.
      // `waitingOrderOf` is that rule; the row's signature card opens the same order (api2/orderSignOff.js), while
      // `order_number` below stays the line's own order, which eleven call sites post back.
      const signing = waitingOrderOf(row) || row.order;
      const orderStatus = signing
        ? (LEGACY_STATUS_MAP[signing.status] || signing.status || '')
        : legacyStatus;
      const nurseSig = signing?.signatures_summary?.nurse?.signature_name
        ?? signing?.nurse_signature_name
        ?? signing?.nurse_signature
        ?? row.nurse_signature
        ?? '';
      const providerSig = signing?.signatures_summary?.provider?.signature_name
        ?? signing?.provider_signature_name
        ?? signing?.provider_signature
        ?? row.provider_signature
        ?? '';
      // The order's day; the line's own start when no order was loaded. The
      // API's `ordered_on` is the organization's day; the instant is UTC, so
      // its fallback is its local day — the UTC day of an order written after
      // 20:00 in New York is tomorrow.
      const orderedAt = row.order?.ordered_at ? toLocalYMD(row.order.ordered_at) : null;
      const orderDate = row.order?.ordered_on ?? orderedAt ?? row.start_on ?? '';
      const orderedTime = row.order?.ordered_time ? String(row.order.ordered_time).slice(0, 8) : '';
      const prescriber = row.order?.prescriber?.name ?? row.prescriber_name ?? '';
      const refills = row.prescription?.refills ?? row.prescription?.refills_authorized ?? row.refills ?? '';
      const orderId = row.order_id === null || row.order_id === undefined ? '' : String(row.order_id);

      return {
        ...row,
        status: legacyStatus,
        // main's `ifOrderSigned` (MED-19, MED-OM-03) reads these three; without
        // them the MAR's half-signed guard never fired.
        orderstatus: orderStatus,
        nursesignature: nurseSig,
        providersignature: providerSig,
        ordernumber: orderId,
        order_number: orderId,
        orderDate,
        orderdate: orderDate,
        // The time beside that day, on the same clock (the organization's). Current Medications picks a medication's
        // latest row by order date and then order time (currentMedsLatest newestFirst): a change order written the
        // day of its order tied on the date, and the line it replaced could win (NUR339-18).
        ordertime: orderedTime,
        orderTime: orderedTime,
        primaryPhysician: prescriber,
        prescriberName: prescriber,
        refills,
        // On hand, which the pre-pour sheet compares with the doses to pour:
        // unaliased, every medication read as short.
        available: row.available_quantity ?? row.total_quantity ?? 0,
        tabsAvailable: row.available_quantity ?? row.total_quantity ?? 0,
        total: row.total_quantity ?? 0,
        tabletqnty: row.tablet_quantity ?? row.total_quantity ?? 0,
        ndcnumber: row.ndc_code ?? '',
        ndcNumber: row.ndc_code ?? '',
        rxnorns: row.rxnorm_code ?? '',
        // The pre-pour sheet's own reads (PP-11). Main's GetPatientMeds is
        // `medications.* + orders.* + paproviders.* + patpharmacy.*`, and the
        // sheet's Non-Rx grouping and its More cards read those names
        // (utils/prepourMedRecord.js); unaliased, every card said "Not
        // recorded" and every medication grouped as a prescription.
        nonprescription: row.is_unordered ? 1 : 0,
        instruction: row.instructions ?? '',
        med_enddate: row.end_on ?? '',
        providername: row.order?.prescriber?.name ?? '',
        providernpi: row.order?.prescriber?.npi ?? '',
        provideroffice: row.order?.prescriber?.phone ?? '',
        pharmacyname: row.prescription?.pharmacy_snapshot?.name ?? row.prescription?.pharmacy?.name ?? null,
        pharmacyAddress: row.prescription?.pharmacy_snapshot?.address ?? null,
        pharmacyPhone: row.prescription?.pharmacy?.phone ?? null,
        pharmacyNpi: row.prescription?.pharmacy_snapshot?.npi ?? null,
        // The flags main's MAR sends back on every time edit (CRIT-06, MED-20,
        // FEM-34). Absent, `isAdminFlagOn` read 0 and each edit took the
        // medication off the MAR and cleared pre-pour and nebulizer with it.
        medadmin: row.requires_administration ? 1 : 0,
        administration: row.requires_administration ? 1 : 0,
        prepour: row.is_prepour_eligible ? 1 : 0,
        nebulizer: row.is_nebulizer ? 1 : 0,
        creatorName: row.created_by_name ?? '',
        // The medication's own routing key. `MedicationResource` emits it as
        // `id` and never as `medentryid`, but every medication-scoped write
        // reads the legacy spelling off these rows — holds (MedAdmin.vue:4307,
        // :4453, :4467), discontinue (:5396) and the PRN sign-off (:4998) — so
        // without these two aliases each of those paths resolves to
        // `/medications/null/...`.
        medentryid: row.id,
        medicationid: row.id,
        medname: row.name,
        medName: row.name,
        med_medname: row.name,
        med_amount: row.dose_amount,
        quantity: row.total_quantity,
        med_doseuom: row.dose_unit,
        med_frequency: row.frequency_code,
        frequency: row.frequency_code,
        med_startdate: row.start_on,
        startDate: row.start_on,
        endDate: row.end_on,
        diagnose_code: row.diagnosis_code,
        writer: row.created_by_name,
        // The MAR grid branches on the string `'1'` (MedAdmin.vue:3486, :3568):
        // a PRN drug has no scheduled columns and its cells come only from what
        // was actually given. Read off a boolean `is_prn`, every PRN medication
        // took the scheduled path and rendered a row of empty slots it never
        // had.
        prn: row.is_prn ? '1' : '0',
        // The dose times, as the `[{time}]` array the grid parses. Present only
        // when the spec asked for `?include=administration_schedule`; an absent
        // key is the honest answer for a read that did not fetch the schedule,
        // and the grid's own `|| '[]'` fallback covers it.
        ...(row.administration_schedule ? { yearmedtime: scheduleSlots(row) } : {}),
      };
    });
}

/** The cell statuses that record an administration outcome. */
const ADMINISTERED_CELL_STATUSES = new Set(['taken', 'refused', 'missed']);

/**
 * The current medications, with each course's given doses on its current line
 * (MED-15, MED-OM-02, PP-18).
 *
 * The MAR's today view and the pre-pour sheet list what is *current*: a
 * superseded line beside its successor is the same drug twice, shown as active
 * and pourable — a double pour and a double stock decrement. So superseded
 * lines are dropped here.
 *
 * But a revision leaves each dose already given on the line it was given
 * under (the server will not re-attribute a signed administration), and the
 * successor's template still lists that time, so the grid would draw it as due
 * — the dose the patient has had, offered again. The server refuses the second
 * recording; this keeps the screen from asking. Each given, refused or missed
 * cell of a superseded line is shown on the line that replaced it (following
 * the chain). Where that line has a *blank* cell at the same day and time, the
 * given dose is shown instead of it: the blank one is the dose already had,
 * and drawing it as due is the double dose (UHjWAgbK). Where it has a recorded
 * cell of its own, that cell stays. Read-only: nothing is re-attributed in the
 * record.
 */
export function currentCourseRows(rows, cells) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const successorOf = new Map();

  list.forEach((row) => {
    if (row.status === 'superseded' && row.superseded_by_order_medication_id) {
      successorOf.set(Number(row.id), Number(row.superseded_by_order_medication_id));
    }
  });

  const currentId = (id) => {
    let current = Number(id);
    const seen = new Set();
    while (successorOf.has(current) && !seen.has(current)) {
      seen.add(current);
      current = successorOf.get(current);
    }
    return current;
  };

  const allCells = (Array.isArray(cells) ? cells : []).filter(Boolean);
  const slotKey = (medication, cell) => `${medication}|${cell.administered_on}|${String(cell.scheduled_time || '').slice(0, 5)}`;
  const isBlank = (cell) => !cell.status || String(cell.status) === 'pending';

  // The current lines' own scheduled cells, by slot.
  const ownCell = new Map();
  allCells.forEach((cell) => {
    const medication = Number(cell.order_medication_id);
    if (!successorOf.has(medication) && cell.status_tag !== 'prn') {
      ownCell.set(slotKey(medication, cell), cell);
    }
  });

  // Which superseded-line doses are shown, and which slots they take over.
  const shown = new Set();
  const claimed = new Set();
  allCells.forEach((cell) => {
    const medication = Number(cell.order_medication_id);
    if (!successorOf.has(medication)) return;
    if (!ADMINISTERED_CELL_STATUSES.has(String(cell.status)) || cell.status_tag === 'prn') return;

    const key = slotKey(currentId(medication), cell);
    const own = ownCell.get(key);

    if ((own && !isBlank(own)) || claimed.has(key)) return;
    claimed.add(key);
    shown.add(cell);
  });

  const folded = allCells.flatMap((cell) => {
    const medication = Number(cell.order_medication_id);

    if (!successorOf.has(medication)) {
      const replaced = cell.status_tag !== 'prn' && isBlank(cell) && claimed.has(slotKey(medication, cell));
      return replaced ? [] : [cell];
    }

    return shown.has(cell) ? [{ ...cell, order_medication_id: currentId(medication) }] : [];
  });

  return {
    rows: list.filter((row) => row.status !== 'superseded'),
    cells: folded,
  };
}

// --- Enum translations -----------------------------------------------------
// keyon stored these as free varchar and the SPA still sends the legacy
// spellings. Each map below is the legacy vocabulary on the left and the
// port's `Rule::in` vocabulary on the right; an unknown value is passed through
// so the API's own 422 names it rather than the bridge inventing a default.

const NOTE_TYPES = {
  nursing: 'nursing',
  providers: 'provider',
  provider: 'provider',
  // Main #324's three caregiver visit notes (MX-07). The popup's option is
  // 'Home Health Aid'; 'Aide' is the account type's other spelling.
  'home health aid': 'home_health_aide',
  'home health aide': 'home_health_aide',
  'allied professional': 'allied_professional',
  'geriatric care manager': 'geriatric_care_manager',
  vitals: 'vitals',
  // The two legacy literals for the remote-monitoring note types, which are the
  // only ones that carry a stopwatch. `Note::TYPE_THERAPEUTIC_MONITORING`
  // records the mapping from the other side.
  '9898x': 'therapeutic_monitoring',
  '9945x': 'physiologic_monitoring',
};

export const noteType = (v) => NOTE_TYPES[String(v ?? '').toLowerCase()] ?? v;

/**
 * A note's type in the words the notes screens filter and label on - the
 * popup's own values ('Nursing', 'Providers', '9945X') - from the API's
 * `Note::TYPES` slug. NotesPage and ProfessionalNotesPage compare `note.type`
 * against what the popup saved and print '9945X' as 'Vitals - 9945X', so a
 * slug there matched nothing and labelled itself. An unknown value passes
 * through, as `noteType` does the other way.
 */
const NOTE_TYPE_LABELS = {
  nursing: 'Nursing',
  provider: 'Providers',
  home_health_aide: 'Home Health Aid',
  allied_professional: 'Allied Professional',
  geriatric_care_manager: 'Geriatric Care Manager',
  vitals: 'Vitals',
  therapeutic_monitoring: '9898X',
  physiologic_monitoring: '9945X',
};

export const noteTypeLabel = (v) => NOTE_TYPE_LABELS[String(v ?? '')] ?? v;

/**
 * The note stopwatch, in the unit the API's field name claims.
 *
 * `NotePopup`'s timer increments once a second (NotePopup.vue:1139) and keyon
 * billed `timeLength` against a 1200-**second** cycle (SqlClass.php:6179), but
 * the value was posted into `duration_minutes`, which `NoteLedger` multiplies by
 * 60 to get `billable_seconds` — a 60x inflation of billable RPM/RTM time, and a
 * `max:1440` rejection of any note timed for more than 24 minutes.
 */
export function billableMinutes(seconds) {
  const n = Number(seconds);

  if (!Number.isFinite(n) || n <= 0) return null;

  // Two decimals: `billable_seconds` is an integer column and the API multiplies
  // by 60, so this round-trips a whole number of seconds to within a second.
  return Math.round((n / 60) * 100) / 100;
}

const DIAGNOSIS_STATUSES = {
  active: 'active',
  // A diagnosis line on a not-yet-signed order. `OrderDiagnosis::STATUSES` has
  // no 'pending' member — the order carries the pending state, the line does
  // not — so it enters active and moves with its order.
  pending: 'active',
  resolved: 'resolved',
  inactive: 'inactive',
  discontinued: 'discontinued',
};

export const diagnosisStatus = (v) => DIAGNOSIS_STATUSES[String(v ?? '').toLowerCase()] ?? v;

const PROCEDURE_STATUSES = {
  pending: 'pending',
  active: 'active',
  completed: 'completed',
  cancelled: 'cancelled',
  discontinued: 'discontinued',
  superseded: 'superseded',
};

export const procedureStatus = (v) => PROCEDURE_STATUSES[String(v ?? '').toLowerCase()] ?? v;

const CONDITION_STATUSES = {
  active: 'active',
  // A chart quick-add asserts the problem now; the legacy 'Pending' belonged to
  // the *order* the line sat on, and a problem-list row has no order.
  pending: 'active',
  recurrence: 'recurrence',
  relapse: 'relapse',
  inactive: 'inactive',
  remission: 'remission',
  resolved: 'resolved',
};

export const conditionStatus = (v) => CONDITION_STATUSES[String(v ?? '').toLowerCase()] ?? v;

// --- The structured allergy form -------------------------------------------
//
// `AllergyForm.vue` is a Vue 3 `script setup` component whose option values are
// also the union members of `Allergy` in `stores/allergyStore.ts`, which the
// allergy list and card render from. Changing the option values would have to
// change that type and every component bound to it, so the vocabulary is
// translated on the way out instead — which is also where the same translation
// has to live for the other producers of an `allergyBody` payload.
//
// Every one of these is a real 422 today: the port validates each field with
// `Rule::in` against `App\Models\Condition`, and the most common combination a
// clinician records — type Drug, severity Life-Threatening — fails two of them,
// so a medication allergy specifically cannot be written.

const ALLERGY_TYPES = {
  // `Condition::ALLERGY_TYPES` is the FHIR-flavoured vocabulary; the form still
  // offers the two words the legacy varchar column happened to hold.
  drug: 'medication',
  medication: 'medication',
  environmental: 'environment',
  environment: 'environment',
  food: 'food',
  biologic: 'biologic',
  other: 'other',
};

export const allergyType = (v) => ALLERGY_TYPES[String(v ?? '').toLowerCase()] ?? v;

const ALLERGY_SEVERITIES = {
  mild: 'mild',
  moderate: 'moderate',
  severe: 'severe',
  'life-threatening': 'life_threatening',
  life_threatening: 'life_threatening',
  unknown: 'unknown',
};

export const allergySeverity = (v) => ALLERGY_SEVERITIES[String(v ?? '').toLowerCase()] ?? v;

const ALLERGY_CRITICALITIES = {
  low: 'low',
  high: 'high',
  'unable-to-assess': 'unable_to_assess',
  unable_to_assess: 'unable_to_assess',
};

export const allergyCriticality = (v) => ALLERGY_CRITICALITIES[String(v ?? '').toLowerCase()] ?? v;

const ALLERGY_VERIFICATIONS = {
  unconfirmed: 'unconfirmed',
  provisional: 'provisional',
  differential: 'differential',
  confirmed: 'confirmed',
  refuted: 'refuted',
  'entered-in-error': 'entered_in_error',
  entered_in_error: 'entered_in_error',
};

export const allergyVerification = (v) => ALLERGY_VERIFICATIONS[String(v ?? '').toLowerCase()] ?? v;

/**
 * The form's Status select against `Condition::CLINICAL_STATUSES`.
 *
 * "Entered in Error" is offered on *both* selects but is only a member of the
 * verification vocabulary — FHIR's AllergyIntolerance invariant is that a record
 * marked entered-in-error carries no clinical status at all, and the port
 * follows it: `CLINICAL_STATUSES` has no such member and the value 422s.
 *
 * It cannot simply be dropped either: the clinician is asserting the entry is
 * wrong and expects it off the current list. `inactive` is the member that does
 * that (it is absent from `Condition::OPEN_STATUSES`, which is what puts an
 * entry on the current allergy list) and the *reason* travels in
 * `verification_status`, which is where FHIR puts it — see `allergyRetraction`.
 */
const ALLERGY_CLINICAL_STATUSES = {
  active: 'active',
  inactive: 'inactive',
  resolved: 'resolved',
  recurrence: 'recurrence',
  relapse: 'relapse',
  remission: 'remission',
  'entered-in-error': 'inactive',
  entered_in_error: 'inactive',
};

export const allergyClinicalStatus = (v) => ALLERGY_CLINICAL_STATUSES[String(v ?? '').toLowerCase()] ?? v;

/** Whether the form's Status select is asserting the whole entry is a mistake. */
export const allergyRetraction = (v) => ['entered-in-error', 'entered_in_error']
  .includes(String(v ?? '').toLowerCase());

/**
 * The reactions, as the API takes them (Trello As9Gu4Tx).
 *
 * `AllergyForm` collects `[{manifestation, severity}]` (AllergyForm.vue:129),
 * which is what main stores, and the API now takes exactly that -
 * `{manifestation, manifestation_code?, severity}` per reaction
 * (App\Domain\Clinical\AllergyReactions). This used to strip each reaction to
 * its manifestation because `reactions.*` was a string, losing every
 * per-reaction severity on every save, a migrated allergy's included. Each
 * severity goes through the allergy's own vocabulary map ('life-threatening'
 * to 'life_threatening'); one the map does not know is passed through so the
 * API's 422 names it. A bare term is still a reaction, with no severity.
 */
export function allergyReactions(value) {
  if (value === null || value === undefined || value === '') return undefined;

  const raw = Array.isArray(value) ? value : [value];

  return raw
    .map((entry) => {
      if (entry && typeof entry === 'object') {
        const manifestation = String(entry.manifestation ?? '').trim();
        if (!manifestation) return null;

        const reaction = { manifestation };
        const code = String(entry.manifestation_code ?? entry.manifestationCode ?? '').trim();
        if (code) reaction.manifestation_code = code;
        if (entry.severity !== null && entry.severity !== undefined && entry.severity !== '') {
          reaction.severity = allergySeverity(entry.severity);
        }

        return reaction;
      }

      const term = String(entry ?? '').trim();

      return term ? { manifestation: term } : null;
    })
    .filter(Boolean);
}

/**
 * A code picker's single field, split back into a code and a description.
 *
 * The ICD-10 and CPT search boxes write the whole label into the input they
 * post — `diagSearchDescrip = diag.code + " - " + diag.description`
 * (CurrentDiag.vue:322, CurrentProc.vue:671, and `${proc.code} ${proc.description}`
 * at CurrentProc.vue:34) — so `diagcode` on the wire is
 * `'E11.9 - Type 2 diabetes mellitus without complications'`, not `'E11.9'`.
 * keyon stored the string verbatim in a varchar; the port's code columns are
 * `max:32` and the description is its own field, so a whole label sent as a code
 * is a 422 naming `icd10_code`.
 *
 * A value with no recognisable code is returned as description only, rather
 * than as a 46-character "code": the clinician typed a term, and the endpoints
 * keep unmatched text in `uncoded_text` on purpose.
 */
export function codedTerm(value) {
  const text = String(value ?? '').trim();

  if (!text) return { code: null, text: null };

  // 'E11.9 - Type 2 diabetes' — an explicit separator, whatever the first token
  // looks like.
  const dashed = /^(\S+)\s+[-–—]\s+(.+)$/.exec(text);

  if (dashed) return { code: dashed[1], text: dashed[2].trim() };

  // '99213 Office visit' — no separator, so the first token has to look like a
  // code (a digit in it, no spaces) before it is treated as one.
  const spaced = /^([A-Za-z]?\d[\dA-Za-z.]*)\s+(.+)$/.exec(text);

  if (spaced) return { code: spaced[1], text: spaced[2].trim() };

  return text.length <= 32 ? { code: text, text } : { code: null, text };
}

/**
 * Whether a legacy `InsertDiagnosis` payload is really a procedure.
 *
 * keyon kept procedures in `orders_diagnosis` behind a `diagnosis_type`
 * discriminator spelt `'Diagnosis'` / `'Procedure'` (SqlClass.php:16778). The
 * port split the table in two, and repurposed the *name* `diagnosis_type` to
 * mean primary-versus-secondary rank — so the same field now selects the
 * endpoint rather than travelling in the body.
 */
export const isProcedureLine = (p) => String((p || {}).diagnosis_type ?? '').toLowerCase() === 'procedure';

const DISCONTINUATION_TYPES = {
  discontinue: 'discontinue',
  expire: 'expire',
  not_approved: 'not_approved',
  // `holdobjec.type` on a discontinuation is the legacy hold *scope*
  // ('all' | 'specific'), never a change type — MedAdmin.vue:5391 hard-codes
  // 'all'. Discontinuing part of a day is still a discontinuation.
  all: 'discontinue',
  specific: 'discontinue',
};

export const discontinuationType = (v) => DISCONTINUATION_TYPES[String(v ?? '').toLowerCase()] ?? 'discontinue';

/**
 * `'2026-08-31'` + `'14:30'` -> `'2026-08-31T14:30'`, which `date` accepts.
 *
 * Its one caller now is the order header's `ordered_at` (clinicalBridge.js
 * `orderHeader`). The value carries no zone on purpose: it is the day and the
 * time the form shows, and the API composes a zone-less `ordered_at` on the
 * **organization's** wall clock (pacmny-api `OrderedAt`), as it composes a
 * dose's `time` (`wallClockTime`). The MAR's doses send the local day and the
 * wall-clock time apart instead.
 */
export function instantAt(date, time) {
  const day = dateOnly(date);

  if (!day) return null;

  const match = /^(\d{1,2}):(\d{2})/.exec(String(time ?? '').trim());

  if (!match) return day;

  return `${day}T${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
}

// --- Dates -----------------------------------------------------------------
// The new API speaks ISO-8601 with an offset. The legacy UI parses several
// zone-less formats, so conversions happen here rather than in components.

export function dateOnly(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD HH:MM:SS' in the viewer's local zone, which is what the old UI showed. */
export function legacyDateTime(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
       + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export const hhmm = (v) => (v ? String(v).slice(0, 5) : null);
export const hhmmss = (v) => {
  if (!v) return null;
  const s = String(v);
  return s.length === 5 ? `${s}:00` : s;
};

/**
 * Rename keys on every row of a collection.
 *
 * The single most common shaping job: the new API returns `first_name` where a
 * grid column is bound to `firstname`.
 */
export function rekey(rows, mapping) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((row) => {
    const out = { ...row };
    Object.entries(mapping).forEach(([from, to]) => {
      if (from in row) {
        out[to] = row[from];
        if (from !== to) delete out[from];
      }
    });
    return out;
  });
}

// --- Care-team memberships -------------------------------------------------

/**
 * The `care_assignments` id for one caregiver on one chart, out of a care-team
 * response.
 *
 * `PATCH /patients/{patient}/care-team/{member}` binds a `CareAssignment` by its
 * own primary key and then asserts it is on this chart
 * (CareTeamMemberController.php:32). The legacy operation addressed the member
 * by *user* id — `member_unique_ID` — because keyon's `providerassignment` rows
 * had no identity the client ever saw, so the payload simply does not carry the
 * key the route needs. Sending the user id there resolves a different
 * membership whenever the two numbers collide inside the tenant, which is an
 * edit written onto the wrong person's chart, and 404s otherwise.
 *
 * The team read is the only place the two ids appear together, which is why
 * this is a lookup rather than an alias: `careTeamSlot` cannot put the
 * assignment id into `member_unique_ID` because six other operations
 * round-trip that field into routes that bind a **user**
 * (`care-team/{role}/{user}`, message groups, patient groups).
 */
export function careTeamAssignmentId(team, userId) {
  const target = id(userId);

  if (target === null || !team || typeof team !== 'object') return null;

  const match = Object.values(team)
    .filter(Array.isArray)
    .flat()
    .find((member) => member && id(member.user_id) === target);

  return match ? routeId(match.id) : null;
}

// --- Per-user workspace state ----------------------------------------------

/**
 * A client-owned blob, as the object the `present|array` rules accept.
 *
 * The legacy actions posted a **string**: `saveState` sends
 * `JSON.stringify(instance.root)` (store/modules/fsm/index.js:429) and keyon
 * wrote it into a text column, with the client parsing it back on the way out
 * (fsm/index.js:494). `ReplaceUiStateRequest` and `ReplacePreferencesRequest`
 * both validate an array, and PHP builds one from a JSON object or a JSON list
 * and from nothing else — so every workspace save 422'd and the FSM's persisted
 * tree was never written at all.
 *
 * A value that is not JSON is wrapped under `raw` rather than dropped. It is
 * unreachable for anything `JSON.stringify` produced, which is every real
 * caller; keeping it means a blob can never be lost silently on its way to a
 * store whose whole job is not to lose it.
 */
export function stateBlob(value) {
  if (value === null || value === undefined || value === '') return {};
  if (typeof value === 'object') return value;

  try {
    const parsed = JSON.parse(String(value));

    return parsed !== null && typeof parsed === 'object' ? parsed : { raw: parsed };
  } catch (e) {
    return { raw: String(value) };
  }
}

// --- CPT billing -----------------------------------------------------------

/**
 * One patient's billing cycles, in the three maps the CPT billing grid indexes.
 *
 * `GetPatCpt2` (SqlClass.php:6727-6771) returned `current[cpt]`,
 * `historical[cpt][fid]` and `cptList[cpt][] = [meter, meterName]`, and
 * `GetPatientCptBilling2` added `icdList` from `cpt_meters`
 * (processClass.php:3210-3222). The template drives its whole second-level
 * accordion off `cptList`, so a response without it renders **nothing** even
 * when the cycles arrived.
 *
 * `status` is the legacy 0/1/2 the grid prints as Not Billable / Billable /
 * Billed, rebuilt from the port's named states rather than stored as an
 * unnamed int.
 *
 * `cptList` is derived from the cycles themselves, and that is a real
 * narrowing: a meter is enrolled under a code through
 * /patients/{patient}/cpt-meters (AddMeterCpt, AddPatientCpt; CptMeterEnrolment
 * writes `cpt_meters` and the `cpt_assignments` row the ledger bills under,
 * and GetMeterCpt reads them back for the CPT page), but an enrolment opens no
 * cycle, so a code assigned to a device that has never accrued cannot appear
 * here. Deriving it from cycles is what makes the codes that DID accrue
 * visible; inventing an enrolment list would claim assignments nobody made.
 */
export function cptBillingBuckets(entries, pid) {
  const current = {};
  const historical = {};
  const cptList = {};
  const icdList = [];
  const seenIcd = new Set();
  const seenMeter = new Set();

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const cpt = entry && entry.cpt_code;

    // A voided cycle is not a billing state the legacy grid has a word for, and
    // its three labels are all claims about a live cycle — rendering it as "Not
    // Billable" would put a withdrawn cycle on a revenue screen as though it
    // were merely still accruing.
    if (!cpt || entry.state === 'voided') return;

    // The grid keys the ICD picker off `record.pid`, and it has to be the
    // same value the response is keyed by — the caller's own id — or
    // `cptData[billingRecord.pid]` is undefined when the Bill dialog opens.
    const record = cptCycleRow(entry, pid);

    cptList[cpt] = cptList[cpt] || [];

    if (entry.device_model && !seenMeter.has(`${cpt} ${entry.device_model}`)) {
      seenMeter.add(`${cpt} ${entry.device_model}`);
      // `[meter, meterName]` — the grid prints `${meter[0]} (${meter[1]})`. The
      // port keeps the device *model* on a cycle, not the device id the legacy
      // `cpt_meters.meter` held, so both halves are the one fact there is.
      cptList[cpt].push([entry.device_model, entry.device_model]);
    }

    if (entry.icd10_code && !seenIcd.has(`${cpt} ${entry.icd10_code}`)) {
      seenIcd.add(`${cpt} ${entry.icd10_code}`);
      icdList.push({ cpt, icd: entry.icd10_code });
    }

    if (entry.is_billed) {
      historical[cpt] = historical[cpt] || {};
      historical[cpt][String(entry.id)] = record;

      return;
    }

    // `cpt_billing2` held exactly one open cycle per code; the port can hold
    // several only while one is rolling over, so the latest wins the slot the
    // grid has for it.
    if (!current[cpt] || String(entry.cycle_start_on) > String(current[cpt].firstDate)) {
      current[cpt] = record;
    }
  });

  return { current, historical, cptList, icdList };
}

/**
 * The legacy `status int`: 0 not billable, 1 billable, 2 billed.
 *
 * The port names the same three states (`BillingEntry::STATE_*`) and the grid's
 * click handler gates on 1, so a cycle still accruing must not read as
 * billable — that is the number a biller would submit a claim from.
 */
function cptBillingStatus(entry) {
  if (entry.is_billed || entry.state === 'billed') return 2;

  return entry.is_threshold_met || entry.state === 'threshold_met' ? 1 : 0;
}

/**
 * One billing cycle as a row of main's cycle tables.
 *
 * `cpt_billing`, `cpt_billing2` and their history twins held `fid, pid, cpt,
 * cost, status, firstDate, lastDate, cycleCount, latestDateAdded, icd,
 * fidLastCycle, fidNextCycle` (v1 adds `meterid`), and the CPT screens read
 * them by those names: CptPage prints firstDate, lastDate, cycleCount and
 * status; CptDesc prints cost and latestDateAdded and walks fidLastCycle /
 * fidNextCycle; the billing grid prints the dates and the status. The API's
 * CptEntryResource names the same facts otherwise, so a row carries both: its
 * own keys stay, and main's are added from them. One definition, so the CPT
 * page and the billing grid cannot read a cycle two ways.
 *
 * `cycleCount` is `accrued_value`, in the unit `accrual_basis` names (days of
 * measurement, or seconds of service time) - the unit the legacy column mixed,
 * which CptPage's formatCycleCount already tells apart by code. `meterid` is
 * the cycle's `device_model`: the one fact v1 had that v2 lacked, and what
 * billing_entries.device_model is documented as. The chain links are left empty
 * here; they need the cycle's neighbours (cptPageTables).
 */
function cptCycleRow(entry, pid) {
  return {
    ...entry,
    pid,
    cpt: entry.cpt_code,
    fid: entry.id,
    cost: entry.amount,
    status: cptBillingStatus(entry),
    firstDate: entry.cycle_start_on,
    lastDate: entry.cycle_end_on,
    cycleCount: entry.accrued_value,
    latestDateAdded: entry.last_accrued_on,
    icd: entry.icd10_code,
    meterid: entry.device_model || null,
    fidLastCycle: null,
    fidNextCycle: null,
  };
}

/** Oldest cycle first. A code has one cycle per start date (billing_entries is unique on it), so the id only settles a repeat. */
function byCycleStart(a, b) {
  const x = String(a.firstDate ?? '');
  const y = String(b.firstDate ?? '');

  if (x !== y) return x < y ? -1 : 1;

  return Number(a.fid) - Number(b.fid);
}

/**
 * The CPT screens' cycle tables: `{current, historical, cptList}`.
 *
 * CptPage (GetPatientCpt2) reads `current[cpt]` and `historical[cpt][fid]`; the
 * device page (GetPatientCpt) reads `current[meter][cpt]` and
 * `historical[meter][cpt][fid]`, and `byMeter` picks between them. The API
 * answers a flat list of cycles instead, and the tables are built from it the
 * way main's were kept:
 *
 *  - `cpt_billing2` held ONE row per code, the code's latest cycle whether or
 *    not it had been billed (MarkBilled sets its status in place; the next
 *    period's accrual copies the row to the history table and opens a new one),
 *    and the history table held the cycles before it. So each group (a code, or
 *    a meter and a code) puts its live cycle - open or threshold met, the latest
 *    if a rollover left two - in `current`, and the others in `historical`
 *    keyed by id. A group with no live cycle gives its latest billed one to
 *    `current`: both screens read `current[...].fid` whenever history exists
 *    (CptPage.getHistoricalData, UserDevices.showCptDesc) and throw on an
 *    undefined, and "Billed" is what that code's latest cycle is.
 *  - Keyed by `device_model` for the device page, '' when a cycle names no
 *    device (a cycle opened by hand: its legacy meterid was NULL, which PHP
 *    files under ''). The API names no `device_id`; keying on one filed every
 *    cycle under '' and left each code only the last cycle written.
 *  - `fidLastCycle` / `fidNextCycle` chain a group by date, which is what
 *    CptDesc's View Previous / Next Cycle walk through `historicalInfo[fid]`.
 *    The API stores the same links (previous_entry_id, next_entry_id) but does
 *    not send them, and its chain also runs through cycles this group does not
 *    hold (another meter's, a voided one); the group's own order links only
 *    cycles the screen has, so every link it shows can be followed.
 *  - A voided cycle is a withdrawn claim and is left out, as in
 *    cptBillingBuckets; so is one that names no code.
 *
 * `cptList` is derived from the cycles, with the narrowing cptBillingBuckets
 * documents: a code enrolled on a device that has not accrued has no cycle to
 * derive from (GetMeterCpt is what lists enrolments).
 */
export function cptPageTables(entries, pid, { byMeter = false } = {}) {
  const groups = new Map();
  const seen = new Set();

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const cpt = entry && entry.cpt_code;

    if (!cpt || entry.state === 'voided') return;

    // A cycle opened while the pages were being read shifts every row down a
    // place, so the last row of one page is the first of the next. Filed
    // twice, a cycle would be its own previous cycle.
    if (entry.id !== null && entry.id !== undefined) {
      if (seen.has(entry.id)) return;

      seen.add(entry.id);
    }

    const meter = byMeter ? String(entry.device_model || '') : '';
    const key = JSON.stringify([meter, cpt]);
    const group = groups.get(key) || { meter, cpt, cycles: [] };

    group.cycles.push(cptCycleRow(entry, pid));
    groups.set(key, group);
  });

  const current = {};
  const historical = {};
  const cptList = {};
  const tableFor = (table, meter) => {
    if (!byMeter) return table;

    table[meter] = table[meter] || {};

    return table[meter];
  };

  groups.forEach(({ meter, cpt, cycles }) => {
    cycles.sort(byCycleStart);
    cycles.forEach((row, index) => {
      row.fidLastCycle = index > 0 ? cycles[index - 1].fid : null;
      row.fidNextCycle = index < cycles.length - 1 ? cycles[index + 1].fid : null;
    });

    const live = cycles.filter((row) => row.status !== 2);
    const pool = live.length > 0 ? live : cycles;
    const latest = pool[pool.length - 1];
    const earlier = cycles.filter((row) => row !== latest);

    tableFor(current, meter)[cpt] = latest;

    if (earlier.length > 0) {
      tableFor(historical, meter)[cpt] = Object.fromEntries(earlier.map((row) => [String(row.fid), row]));
    }

    if (byMeter) {
      cptList[meter] = [...(cptList[meter] || []), cpt];
    } else {
      cptList[cpt] = [...new Set(cycles.map((row) => row.meterid).filter(Boolean))].map((model) => [model, model]);
    }
  });

  return { current, historical, cptList };
}

/** `day` ('YYYY-MM-DD') moved by whole days, with no zone involved. */
function shiftDay(day, by) {
  const [year, month, date] = String(day).slice(0, 10).split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, date + by)).toISOString().slice(0, 10);
}

/**
 * The dates the CPT page's cycle calendar can show readings for: `{from, to}`
 * as the measurements list takes them, or `null` when no cycle is given.
 *
 * Main read every `foradata` row of the chart. The calendar only ever draws the
 * days of a cycle, so the bound is the cycle's own (cptDeviceReadings asks for
 * the one on screen; given several, their first start to their last end). `to`
 * is a date the API reads as that day's midnight, so it names the day AFTER the
 * last one wanted, and a cycle's days are the clinic's calendar days while a
 * reading is an instant in UTC: a day either side keeps a reading near midnight
 * from dropping off the edge. A voided cycle is a withdrawn claim and holds no
 * days.
 */
export function cptCycleSpan(entries) {
  let first = null;
  let last = null;

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const start = entry && entry.state !== 'voided' ? String(entry.cycle_start_on || '') : '';
    const end = String((entry && entry.cycle_end_on) || start);

    if (!/^\d{4}-\d{2}-\d{2}/.test(start) || !/^\d{4}-\d{2}-\d{2}/.test(end)) return;
    if (first === null || start < first) first = start.slice(0, 10);
    if (last === null || end > last) last = end.slice(0, 10);
  });

  return first === null ? null : { from: shiftDay(first, -1), to: shiftDay(last, 2) };
}

/**
 * One device reading as a row of main's `foradata`, in the three fields the CPT
 * page reads.
 *
 * `MDeviceID` is the meter's key as the pages know it - the device's serial
 * number, else its row id (utils/meterRows.js) - because CptPage.getCptMeas
 * matches it to the `meter` an enrolment was made under. A reading with no
 * device matches no meter, as no `foradata` row had a meter that was not a
 * device. `MdateTime` is the reading on the viewer's wall clock, written so
 * that `new Date()` reads it the same in every browser (CptDesc does): main's
 * 'YYYY-MM-DD HH:MM:SS' is not parsed by every one. `MValue1` is the value as
 * reported, the text, not the number a trend line plots (MeasurementResource).
 * The API's own fields stay beside them.
 *
 * @param {object} reading  a MeasurementResource row
 * @param {Map<number, string>} meterKeys  a device's row id to its meter key
 */
export function foradataRow(reading, meterKeys) {
  const local = legacyDateTime(reading.measured_at);

  return {
    ...reading,
    MDeviceID: (meterKeys && meterKeys.get(Number(reading.device_id))) || '',
    MdateTime: local ? local.replace(' ', 'T') : '',
    MDateTimeUTC: reading.measured_at || '',
    MValue1: reading.value === null || reading.value === undefined ? '' : String(reading.value),
  };
}
